import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function JetstarFlightDelaysPage() {
  useSEO({
    title: 'Jetstar Flight Delays Customer Experience | Design For',
    description: 'Improving customer experience during flight disruptions. Strategic consulting for airline customer service and crisis management.',
    keywords: 'flight delays, customer experience, airline industry, crisis management, customer service',
    canonical: '/work/jetstar-flight-delays',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Improving customer experience during flight disruptions',
    description: 'We partnered with Jetstar to develop strategic frameworks for managing customer experience during flight delays and disruptions.',
    url: 'https://example.com/work/jetstar-flight-delays',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Airline - Flight delay customer experience',
    keywords: 'flight delays, customer experience, airline industry, crisis management, customer service',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Improving customer journeys during flight delays
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Jetstar Airlines</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                When flights are delayed or cancelled, it&apos;s one of the most stressful moments for both customers and frontline staff. Information is fragmented, decisions are unclear, and digital tools don&apos;t always support what people actually need in the moment. Jetstar needed a clear digital strategy to make these moments simpler, more human, and easier to manage at scale.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                We focused on improving the end to end customer and staff journey during disruption, from the first message a customer receives, through rebooking, support, and onward travel. Rather than designing a single product, we defined a connected digital approach grounded in real behaviour, operational realities, and commercial priorities.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Conducted 160+ interviews with customers, frontline staff, pilots, engineers, and operations teams',
                  'Defined a clear digital strategy for how Jetstar supports customers and staff during delays and cancellations',
                  'Prioritised 30 high impact digital improvements from 100+ ideas',
                  'Designed clear journey principles to guide communications, self service, and staff tools',
                  'Delivered a sequenced digital roadmap across customer messaging, rebooking tools, data, and frontline systems',
                  'Informed a $4.8m, 2 year investment in improving disruption experiences',
                  'Contributed to a 26% improvement in customer feedback during delay and cancellation events',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/9e02e6ffe00a1d7c89715bb190f7985c.png"
                  alt="Improving customer journeys during flight delays — Jetstar Airlines"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/8a0de4efb580a2996c9f4a8333a95293.png"
                  alt="Jetstar customer journey storyboard"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="jetstar-flight-delays" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}